'use client'

import type { ComponentType } from 'react'

import dynamic from 'next/dynamic'

import { FormattedMessage } from '@b-stock/fe-core/bstock-react'

import { DetailsValue } from '@/buyer-portal/components/features/shared'
import { HeaderAlignedPageWidth } from '@/components/PageWidth'
import type { CmsSellerProfile } from '@/helpers/api/fetchPayload'
import { signUpCtaLocationForTab } from '@/helpers/telemetry/signupLinkTracking'

import { useStorefrontId } from '../StorefrontContext'
import { lexicalHasContent } from './cmsSellerProfileAbout'
import { SellerProfileLexicalRichText } from './SellerProfileLexicalRichText'
import { Container, InfoSection, StorefrontTableContainer, Title } from './ui'

// Dynamic so a visitor only downloads the one seller body their storefront resolves to.
const BellShippingDetails = dynamic(() => import('./bellShippingDetails'))
const TmoShippingDetails = dynamic(() => import('./tmoShippingDetails'))

/**
 * Storefront ids are per environment, so a seller is listed once per environment it exists in.
 * Same key the LaunchDarkly `shippingEnabledStorefrontIds` allowlist uses.
 *
 * A storefront the allowlist turns the tab on for but that is absent here has no body of its own —
 * it must not borrow another seller's, so it renders the empty state.
 */
const LEGACY_SHIPPING_BY_STOREFRONT_ID: Record<string, ComponentType> = {
  // T-Mobile — dev, staging, prod. No qa storefront exists for this seller.
  '67d41cd1f27ef0e825a2fdaf': TmoShippingDetails,
  '6a63f164fe43dbbe52e2b180': TmoShippingDetails,
  '6a863bc02f2e4d913d44fcfa': TmoShippingDetails,
  // Wireless Network Marketplace (Bell) — dev, staging, prod. No qa storefront exists.
  '67fe41738fb4fab5c366eb92': BellShippingDetails,
  '6a95bc62c5b1cb8ad5091652': BellShippingDetails,
  '6a99a1690ae82efdcd6f6ce1': BellShippingDetails,
}

export default function ShippingContent({
  cmsSellerProfile,
}: {
  /** CMS row for this seller; its shipping copy replaces the hardcoded content when filled in. */
  cmsSellerProfile?: CmsSellerProfile | null
}) {
  const storefrontId = useStorefrontId()
  const cms = cmsSellerProfile
  const showCms = Boolean(cms && lexicalHasContent(cms.shippingContent))
  const LegacyDetails = storefrontId
    ? LEGACY_SHIPPING_BY_STOREFRONT_ID[storefrontId]
    : undefined

  return (
    <HeaderAlignedPageWidth>
      <StorefrontTableContainer>
        <Title>
          <FormattedMessage id="Storefront.shipping.title" />
        </Title>
        <InfoSection>
          <DetailsValue>
            {showCms && cms ? (
              <Container>
                <SellerProfileLexicalRichText
                  data={cms.shippingContent}
                  signUpCtaLocation={signUpCtaLocationForTab(
                    cms.sellerSlug,
                    'shipping'
                  )}
                />
              </Container>
            ) : LegacyDetails ? (
              <LegacyDetails />
            ) : (
              <Container>
                <p>
                  <FormattedMessage id="Storefront.shipping.emptyCms" />
                </p>
              </Container>
            )}
          </DetailsValue>
        </InfoSection>
      </StorefrontTableContainer>
    </HeaderAlignedPageWidth>
  )
}
